import axios from "axios";
import { v4 as uuidv4 } from "uuid";

// API config
const BASE_URL = process.env.REACT_APP_COPOTEL_API ? `${process.env.REACT_APP_COPOTEL_API}/v1` : '';
const API_KEY = process.env.REACT_APP_COPOTEL_API_KEY || '';
const SERVICE_NAME = process.env.REACT_APP_COPOTEL_API_SERVICE_NAME || "SIS-WEBAPI FRONTEND";
const ENV_NAME = process.env.REACT_APP_COPOTEL_API_ENVIRONMENT_NAME || " ";

// Standard resource attributes following OpenTelemetry semantic conventions
const getResourceAttributes = () => [
  { key: "service.name", value: { stringValue: SERVICE_NAME } },
  { key: "deployment.environment", value: { stringValue: ENV_NAME } },
];

// Check if telemetry is enabled and properly configured
const isTelemetryEnabled = () => {
  return !!(BASE_URL && API_KEY && process.env.NODE_ENV !== 'test');
};

const getHeaders = () => ({
  "Content-Type": "application/json",
  ...(API_KEY && { "Ocp-Apim-Subscription-Key": API_KEY }),
});

const toNano = (ms: number): string => `${ms}000000`;

type KeyValue = {
  key: string;
  value: {
    stringValue: string;
  };
};

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL';

// Enhanced context gathering for richer logs
const getEnhancedAttributes = (customAttributes: KeyValue[] = []): KeyValue[] => {
  const baseAttributes: KeyValue[] = [
    { key: "timestamp", value: { stringValue: new Date().toISOString() } },
    { key: "user_agent", value: { stringValue: navigator.userAgent } },
    { key: "url", value: { stringValue: window.location.href } },
    { key: "pathname", value: { stringValue: window.location.pathname } },
    { key: "search", value: { stringValue: window.location.search } },
    { key: "referrer", value: { stringValue: document.referrer || 'direct' } },
    { key: "viewport", value: { stringValue: `${window.innerWidth}x${window.innerHeight}` } },
    { key: "session_id", value: { stringValue: getSessionId() } },
    { key: "browser", value: { stringValue: getBrowserInfo() } },
    { key: "language", value: { stringValue: navigator.language } },
  ];

  return [...baseAttributes, ...customAttributes];
};

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('otel_session_id');
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem('otel_session_id', sessionId);
  }
  return sessionId;
};

// Get browser information
const getBrowserInfo = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Unknown';
};

// Enhanced logging function with severity levels
export const sendLog = async (
  message: string, 
  level: LogLevel = 'INFO', 
  attributes: KeyValue[] = [],
  error?: Error
) => {
  if (!isTelemetryEnabled()) {
    console.warn('Telemetry is disabled or not properly configured');
    return;
  }

  try {
    // Add error information if provided
    const errorAttributes: KeyValue[] = error ? [
      { key: "error.name", value: { stringValue: error.name } },
      { key: "error.message", value: { stringValue: error.message } },
      { key: "error.stack", value: { stringValue: error.stack || 'No stack trace' } },
    ] : [];

    const allAttributes = getEnhancedAttributes([...attributes, ...errorAttributes]);

    const payload = {
      resourceLogs: [
        {
          resource: {
            attributes: getResourceAttributes(),
          },
          scopeLogs: [
            {
              scope: { name: "enhanced-auto-logger", version: "1.0.0" },
              logRecords: [
                {
                  timeUnixNano: toNano(Date.now()),
                  severityText: level,
                  severityNumber: getSeverityNumber(level),
                  body: { stringValue: message },
                  attributes: allAttributes,
                  traceId: generateTraceId(),
                  spanId: generateSpanId(),
                },
              ],
            },
          ],
        },
      ],
    };

    await axios.post(`${BASE_URL}/logs`, payload, { headers: getHeaders() });
  } catch (error) {
    console.error('Failed to send log to OpenTelemetry:', error);
  }
};

// Helper function to get severity number for log level
const getSeverityNumber = (level: LogLevel): number => {
  switch (level) {
    case 'DEBUG': return 5;
    case 'INFO': return 9;
    case 'WARN': return 13;
    case 'ERROR': return 17;
    case 'FATAL': return 21;
    default: return 9;
  }
};

// Generate trace and span IDs for correlation
const generateTraceId = (): string => uuidv4().replace(/-/g, "").slice(0, 32);
const generateSpanId = (): string => uuidv4().replace(/-/g, "").slice(0, 16);

// Convenience functions for different log levels
export const logDebug = (message: string, attributes?: KeyValue[]) => 
  sendLog(message, 'DEBUG', attributes);

export const logInfo = (message: string, attributes?: KeyValue[]) => 
  sendLog(message, 'INFO', attributes);

export const logWarn = (message: string, attributes?: KeyValue[]) => 
  sendLog(message, 'WARN', attributes);

export const logError = (message: string, error?: Error, attributes?: KeyValue[]) => 
  sendLog(message, 'ERROR', attributes, error);

export const logFatal = (message: string, error?: Error, attributes?: KeyValue[]) => 
  sendLog(message, 'FATAL', attributes, error);

export const sendTrace = async (
  name: string,
  startTime: number,
  endTime: number,
  attributes: KeyValue[] = []
) => {
  if (!isTelemetryEnabled()) {
    console.warn('Telemetry is disabled or not properly configured');
    return;
  }

  try {
    const payload = {
      resourceSpans: [
        {
          resource: {
            attributes: getResourceAttributes(),
          },
          scopeSpans: [
            {
              scope: { name: "auto-tracer" },
              spans: [
                {
                  traceId: uuidv4().replace(/-/g, "").slice(0, 32),
                  spanId: uuidv4().replace(/-/g, "").slice(0, 16),
                  name,
                  startTimeUnixNano: toNano(startTime),
                  endTimeUnixNano: toNano(endTime),
                  attributes,
                },
              ],
            },
          ],
        },
      ],
    };

    await axios.post(`${BASE_URL}/traces`, payload, { headers: getHeaders() });
  } catch (error) {
    console.error('Failed to send trace to OpenTelemetry:', error);
  }
};
export const sendMetric = async (
  name: string,
  value: number,
  attributes: KeyValue[] = []
) => {
  if (!isTelemetryEnabled()) {
    console.warn('Telemetry is disabled or not properly configured');
    return;
  }

  try {
    const payload = {
      resourceMetrics: [
        {
          resource: {
            attributes: getResourceAttributes(),
          },
          scopeMetrics: [
            {
              scope: { name: "auto-metrics" },
              metrics: [
                {
                  name,
                  description: "Custom metric",
                  unit: "count",
                  gauge: {
                    dataPoints: [
                      {
                        value,
                        timeUnixNano: toNano(Date.now()),
                        attributes,
                      },
                    ],
                  },
                },
              ],
            },
          ],
        },
      ],
    };

    await axios.post(`${BASE_URL}/metrics`, payload, { headers: getHeaders() });
  } catch (error) {
    console.error('Failed to send metric to OpenTelemetry:', error);
  }
};

// Convenience functions for common user events
export const logUserAction = (action: string, element?: string, additionalData?: Record<string, any>) => {
  const attributes: KeyValue[] = [
    { key: "event.type", value: { stringValue: "user_interaction" } },
    { key: "user.action", value: { stringValue: action } },
  ];

  if (element) {
    attributes.push({ key: "ui.element", value: { stringValue: element } });
  }

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      attributes.push({ key: `custom.${key}`, value: { stringValue: String(value) } });
    });
  }

  const actionDescription = element ? `${action} on ${element}` : action;
  return logInfo(`User Action: ${actionDescription}`, attributes);
};

export const logPageLoad = (pageName: string, loadTime?: number) => {
  const attributes: KeyValue[] = [
    { key: "event.type", value: { stringValue: "page_load" } },
    { key: "page.name", value: { stringValue: pageName } },
  ];

  if (loadTime) {
    attributes.push({ key: "page.load_time_ms", value: { stringValue: String(loadTime) } });
  }

  return logInfo(`Page Loaded: ${pageName}`, attributes);
};

export const logFormEvent = (formName: string, eventType: 'submit' | 'validation_error' | 'field_change', fieldName?: string, errorMessage?: string) => {
  const attributes: KeyValue[] = [
    { key: "event.type", value: { stringValue: "form_interaction" } },
    { key: "form.name", value: { stringValue: formName } },
    { key: "form.event", value: { stringValue: eventType } },
  ];

  if (fieldName) {
    attributes.push({ key: "form.field", value: { stringValue: fieldName } });
  }

  if (errorMessage) {
    attributes.push({ key: "form.error", value: { stringValue: errorMessage } });
  }

  const fieldDescription = fieldName ? ` - ${fieldName}` : '';
  const logMessage = `Form ${eventType}: ${formName}${fieldDescription}`;
  
  if (eventType === 'validation_error') {
    return logWarn(logMessage, attributes);
  }
  
  return logInfo(logMessage, attributes);
};

export const logBusinessEvent = (eventName: string, entityType?: string, entityId?: string, additionalData?: Record<string, any>) => {
  const attributes: KeyValue[] = [
    { key: "event.type", value: { stringValue: "business_event" } },
    { key: "business.event_name", value: { stringValue: eventName } },
  ];

  if (entityType) {
    attributes.push({ key: "business.entity_type", value: { stringValue: entityType } });
  }

  if (entityId) {
    attributes.push({ key: "business.entity_id", value: { stringValue: entityId } });
  }

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      attributes.push({ key: `business.${key}`, value: { stringValue: String(value) } });
    });
  }

  return logInfo(`Business Event: ${eventName}`, attributes);
};

// Helper function to create enriched log messages
const createEnrichedMessage = (originalMessage: string, keyAttributes: Record<string, string> = {}): string => {
  const sessionId = getSessionId().slice(0, 8); // First 8 chars for brevity
  const browser = getBrowserInfo();
  const timestamp = new Date().toISOString().slice(11, 19); // Just time part
  
  const enrichedParts = [
    originalMessage,
    `Time: ${timestamp}`,
    `Session: ${sessionId}`,
    `Browser: ${browser}`,
  ];

  // Add any key attributes to the message
  Object.entries(keyAttributes).forEach(([key, value]) => {
    enrichedParts.push(`${key}: ${value}`);
  });

  return enrichedParts.join(' | ');
};

// Enhanced logging functions with enriched messages
export const logInfoEnriched = (message: string, keyData: Record<string, string> = {}, attributes: KeyValue[] = []) => {
  const enrichedMessage = createEnrichedMessage(message, keyData);
  return logInfo(enrichedMessage, attributes);
};

export const logErrorEnriched = (message: string, error?: Error, keyData: Record<string, string> = {}, attributes: KeyValue[] = []) => {
  const enrichedMessage = createEnrichedMessage(message, keyData);
  return logError(enrichedMessage, error, attributes);
};

export const logWarnEnriched = (message: string, keyData: Record<string, string> = {}, attributes: KeyValue[] = []) => {
  const enrichedMessage = createEnrichedMessage(message, keyData);
  return logWarn(enrichedMessage, attributes);
};

